/* eslint-disable max-lines */
import { PencilSquareIcon, PlusIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import usePageIndex from "../../hooks/usePageIndex";
import { useSearch } from "../../hooks/useSearch";
import { IEsimPartner } from "../../interfaces/endpoints/IEsimPartner";
import {
  deleteEsimPartner,
  getAllEsimPartners
} from "../../services/EsimPartner.service";
import DeleteModal from "../ui/DeleteModal";
import IconBtn from "../ui/IconBtn";
import Status from "../ui/Status";
import Table, { Row } from "../ui/table";
import Pagination from "../ui/table/Pagination";
import Can from "../users/permissions/Can";

const columns = [
  "Kompania",
  "Email",
  "Statusi",
  "Paketat",
  "Bilanci total",
  "Të dërguara",
  "Veprimet"
];

const EsimPartnersList = () => {
  const { response, loading, handlePageChange, page, setResponse } = useSearch(
    getAllEsimPartners,
    {
      withQuerySearch: true,
      querySelector: "search"
    }
  );
  const { pageIndex } = usePageIndex();

  const handleDelete = (id: number) => {
    // DeleteModal's apiFuncType expects Promise<IAxiosResponse<T>>, but the service
    // returns Promise<boolean> after the axios response unwrapping. Cast to satisfy
    // the generic constraint — same pattern as affiliates list.
    return deleteEsimPartner(id).then((res) => {
      setResponse((prev) =>
        prev
          ? {
              ...prev,
              data: {
                ...prev.data,
                result: prev.data.result.filter(
                  (el: IEsimPartner) => el.id !== id
                )
              }
            }
          : null
      );
      return res;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }) as any;
  };

  return (
    <div className="flex flex-col" style={{ height: "calc(100vh - 11rem)" }}>
      <Table
        titles={columns}
        loading={loading}
        className="overflow-x-auto"
        style="hover"
      >
        {(response?.data.result || []).map((row: IEsimPartner, i: number) => {
          const totalBalance = (row.allocations || []).reduce(
            (sum, a) => sum + a.balanceRemaining,
            0
          );
          const packageCount = (row.allocations || []).length;
          return (
            <Row
              key={i}
              companyName={
                <span className="flex items-center gap-2">
                  {row.logoUrl && (
                    <img
                      src={row.logoUrl}
                      alt={row.companyName}
                      className="h-6 w-6 rounded object-contain bg-gray-50"
                    />
                  )}
                  <span>{row.companyName}</span>
                </span>
              }
              email={row.allowedEmail}
              status={
                <Status
                  type={row.isActive ? "green" : "gray"}
                  name={row.isActive ? "Aktiv" : "Joaktiv"}
                />
              }
              packages={packageCount}
              balance={totalBalance}
              sent={
                <span className="font-semibold tabular-nums">
                  {row.sentCount ?? 0}
                </span>
              }
              actions={
                <span className="flex items-center">
                  <Can permissions="esim.partner.allocation.topup">
                    <Link
                      to={`/esim-partners/${row.id}/allocations`}
                      state={{
                        page: pageIndex || page,
                        crumbs: ["Partneret eSIM", "Menaxho bilancin"]
                      }}
                      title="Menaxho bilancin"
                    >
                      <IconBtn as="span" className="w-6 h-6 text-primary">
                        <PlusIcon className="w-4 h-4" />
                      </IconBtn>
                    </Link>
                  </Can>
                  <Can permissions="esim.partner.partner.update">
                    <Link
                      to={`/esim-partners/edit/${row.id}`}
                      state={{
                        page: pageIndex || page,
                        crumbs: ["Partneret eSIM", "Modifiko partnerin"]
                      }}
                      title="Modifiko"
                    >
                      <IconBtn as="span" className="w-6 h-6">
                        <PencilSquareIcon className="w-4 h-4" />
                      </IconBtn>
                    </Link>
                  </Can>
                  <Can permissions="esim.partner.partner.delete">
                    <DeleteModal
                      handleDelete={() => handleDelete(row.id)}
                      params={row.id}
                      successMessage="Partneri u fshi me sukses"
                      errorMessage="Deshtoi fshirja e partnerit"
                    />
                  </Can>
                </span>
              }
            />
          );
        })}
      </Table>
      <Pagination
        onPageChange={handlePageChange}
        totalCount={response?.data.totalCount || 0}
        length={response?.data?.result?.length}
        page={page}
        pageSize={10}
      />
    </div>
  );
};

export default EsimPartnersList;
